import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, ChefHat, Loader2, AlertCircle, Lightbulb, Leaf, Users, Clock, Wind, Sparkles, Link2, Check } from 'lucide-react';
import ApplianceSelector from '../components/ApplianceSelector';
import AirfryerPanel from '../components/AirfryerPanel';
import RecipeExecutionView from '../components/RecipeExecutionView';
import { recipesApi, convertApi } from '../services/api';
import { useFoodContext } from '../context';
import { TABS } from '../utils/constants';

// AI can return array items as strings or objects — normalize to renderable string
const toText = (v) => {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object') return v.tip || v.text || v.content || v.suggestion || v.optimization || v.alternative || '';
  return String(v ?? '');
};

// Alternative ingredient: string or { original, substitute, note }
const AlternativeItem = ({ alt }) => {
  if (typeof alt === 'string') {
    return <span>{alt}</span>;
  }
  if (alt && typeof alt === 'object' && (alt.original || alt.substitute)) {
    return (
      <span className="flex flex-col gap-0.5">
        <span>
          <span className="line-through text-gray-400">{alt.original}</span>
          <span className="mx-1.5 text-gray-400">→</span>
          <span className="font-medium text-green-800">{alt.substitute}</span>
        </span>
        {alt.note && <span className="text-xs text-gray-500 italic">{alt.note}</span>}
      </span>
    );
  }
  return <span>{toText(alt)}</span>;
};

// Format numbered instruction steps with visual separation
const Instructions = ({ text }) => {
  if (!text) return <p className="text-gray-400 italic text-sm">Instructions non disponibles.</p>;
  const steps = text.split(/(?=\d+\.\s)/).filter(Boolean);
  if (steps.length <= 1) {
    return <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{text}</p>;
  }
  return (
    <ol className="space-y-3">
      {steps.map((step, i) => {
        const clean = step.replace(/^\d+\.\s*/, '').trim();
        return (
          <li key={i} className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-orange-100 text-orange-700 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
              {i + 1}
            </span>
            <span className="text-sm text-gray-700 leading-relaxed">{clean}</span>
          </li>
        );
      })}
    </ol>
  );
};

const Section = ({ title, children }) => (
  <div className="bg-white rounded-2xl shadow-sm p-6">
    <h2 className="font-semibold text-gray-700 mb-4 text-base">{title}</h2>
    {children}
  </div>
);

const ClassicInstructions = ({ instructions }) => (
  <Instructions text={instructions} />
);

const ConvertPanel = ({ recipeId, onConverted }) => {
  const [optimizeCG, setOptimizeCG] = useState(false);
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState(null);

  const handleConvert = async () => {
    setConverting(true);
    setError(null);
    try {
      const result = await convertApi.toAppliance(recipeId, 'airfryer', optimizeCG);
      onConverted(result.appliance_configs);
    } catch (err) {
      setError(err.message);
    } finally {
      setConverting(false);
    }
  };

  return (
    <div className="bg-orange-50 border border-orange-200 rounded-2xl p-6 text-center space-y-4">
      <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto">
        <Wind className="w-7 h-7 text-orange-500" />
      </div>
      <div>
        <h3 className="font-semibold text-gray-800 mb-1">Convertir pour l'Airfryer</h3>
        <p className="text-sm text-gray-500">
          L'IA analysera la recette et générera les réglages optimaux (température, temps, étapes).
          Elle détectera automatiquement si un mode hybride est plus adapté.
        </p>
      </div>

      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={optimizeCG}
          onChange={e => setOptimizeCG(e.target.checked)}
          className="w-4 h-4 accent-green-500"
        />
        <span className="text-sm text-gray-700">
          Optimisation CG — substitutions IG bas + analyse des modes de cuisson
        </span>
      </label>

      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 rounded-xl px-4 py-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <button
        onClick={handleConvert}
        disabled={converting}
        className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white px-6 py-3 rounded-xl font-medium transition-colors shadow-sm"
      >
        {converting ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Génération en cours…</>
        ) : (
          <><Sparkles className="w-4 h-4" /> Convertir pour mon Airfryer</>
        )}
      </button>
    </div>
  );
};

const CopyLinkButton = ({ recipeId }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    const url = `${window.location.origin}${window.location.pathname}#recipe/${recipeId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={handleCopy}
      title="Copier le lien de la recette"
      className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-orange-500 transition-colors px-3 py-1.5 rounded-xl hover:bg-orange-50"
    >
      {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Link2 className="w-3.5 h-3.5" />}
      {copied ? 'Lien copié !' : 'Copier le lien'}
    </button>
  );
};

const RecipeViewPage = ({ recipeId }) => {
  const { setActiveTab } = useFoodContext();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeMode, setActiveMode] = useState('classic');

  const loadRecipe = useCallback(async () => {
    if (!recipeId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await recipesApi.get(recipeId);
      setRecipe(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [recipeId]);

  useEffect(() => { loadRecipe(); }, [loadRecipe]);

  const handleConverted = useCallback((updatedConfigs) => {
    setRecipe(r => ({ ...r, appliance_configs: updatedConfigs }));
    // Auto-select the newly created airfryer or hybrid mode
    const newKey = Object.keys(updatedConfigs).find(k => k !== 'classic' && !recipe?.appliance_configs?.[k]);
    if (newKey) setActiveMode(newKey);
  }, [recipe?.appliance_configs]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
      </div>
    );
  }

  if (error || !recipe) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
        <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
        <p className="text-red-700 font-medium">{error || 'Recette introuvable'}</p>
        <button onClick={() => setActiveTab(TABS.RECIPES)} className="mt-4 text-sm text-red-600 underline">
          ← Retour aux recettes
        </button>
      </div>
    );
  }

  const ingredients = Array.isArray(recipe.ingredients) ? recipe.ingredients : [];
  const cgData = recipe.cg_data || {};
  const applianceConfigs = recipe.appliance_configs || {};
  const activeConfig = applianceConfigs[activeMode];

  const hasHealthTips = Array.isArray(cgData.health_tips) && cgData.health_tips.length > 0;
  const hasAlternatives = Array.isArray(cgData.alternative_ingredients) && cgData.alternative_ingredients.length > 0;

  const renderModeContent = () => {
    if (activeMode === 'classic') {
      return (
        <div className="grid gap-5 lg:grid-cols-2">
          <Section title={`Ingrédients (${ingredients.length})`}>
            <ul className="space-y-2">
              {ingredients.map((ing, i) => (
                <li key={i} className="flex items-center justify-between gap-2 py-1.5 border-b border-gray-50 last:border-0">
                  <span className="text-gray-800 font-medium text-sm">{ing.name}</span>
                  <div className="text-right flex-shrink-0">
                    <span className="text-gray-600 text-sm">{ing.quantity} {ing.unit}</span>
                    {ing.gi_note && <p className="text-xs text-green-600">{ing.gi_note}</p>}
                  </div>
                </li>
              ))}
            </ul>
          </Section>
          <Section title="Préparation classique">
            <ClassicInstructions instructions={recipe.base_instructions} />
          </Section>
        </div>
      );
    }

    // No config yet → show convert panel
    if (!activeConfig) {
      return (
        <ConvertPanel recipeId={recipe.id} onConverted={handleConverted} />
      );
    }

    // Hybrid mode → timeline execution view
    if (activeConfig.type === 'hybrid') {
      return (
        <div className="space-y-5">
          <div className="grid gap-5 lg:grid-cols-2">
            <Section title={`Ingrédients (${ingredients.length})`}>
              <ul className="space-y-2">
                {ingredients.map((ing, i) => (
                  <li key={i} className="flex items-center justify-between gap-2 py-1.5 border-b border-gray-50 last:border-0">
                    <span className="text-gray-800 font-medium text-sm">{ing.name}</span>
                    <span className="text-gray-600 text-sm flex-shrink-0">{ing.quantity} {ing.unit}</span>
                  </li>
                ))}
              </ul>
            </Section>
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="font-semibold text-gray-700 mb-4 text-base">
                {activeConfig.label || 'Mode hybride'}
              </h2>
              <RecipeExecutionView config={activeConfig} />
            </div>
          </div>
        </div>
      );
    }

    // Single appliance mode (airfryer, four, etc.)
    return (
      <div className="grid gap-5 lg:grid-cols-2">
        <Section title={`Ingrédients (${ingredients.length})`}>
          <ul className="space-y-2">
            {ingredients.map((ing, i) => (
              <li key={i} className="flex items-center justify-between gap-2 py-1.5 border-b border-gray-50 last:border-0">
                <span className="text-gray-800 font-medium text-sm">{ing.name}</span>
                <span className="text-gray-600 text-sm flex-shrink-0">{ing.quantity} {ing.unit}</span>
              </li>
            ))}
          </ul>
        </Section>
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <AirfryerPanel config={activeConfig} />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {/* Back + Title */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setActiveTab(TABS.RECIPES)}
            className="flex items-center gap-2 text-gray-500 hover:text-orange-600 text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Retour aux recettes
          </button>
          <CopyLinkButton recipeId={recipe?.id || recipeId} />
        </div>

        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <ChefHat className="w-6 h-6 text-orange-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-gray-800 leading-tight">{recipe.title}</h1>
            <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-500">
              {cgData.servings && (
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" /> {cgData.servings} portion{cgData.servings !== 1 ? 's' : ''}
                </span>
              )}
              {cgData.estimated_gi && (
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" /> IG estimé : {cgData.estimated_gi}
                </span>
              )}
              {cgData.estimated_gl_per_serving && (
                <span className="flex items-center gap-1 text-orange-600 font-medium">
                  CG / portion : {cgData.estimated_gl_per_serving}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Appliance Selector */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <p className="text-xs text-gray-400 uppercase tracking-wide mb-3 font-medium">Mode de cuisson</p>
        <ApplianceSelector
          applianceConfigs={applianceConfigs}
          activeMode={activeMode}
          onModeChange={setActiveMode}
        />
      </div>

      {/* Mode content */}
      {renderModeContent()}

      {/* Health Tips & Alternatives — only in classic mode */}
      {activeMode === 'classic' && (hasHealthTips || hasAlternatives) && (
        <div className="grid gap-5 sm:grid-cols-2">
          {hasHealthTips && (
            <Section title="Conseils glycémiques">
              <ul className="space-y-2">
                {cgData.health_tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <Lightbulb className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                    {toText(tip)}
                  </li>
                ))}
              </ul>
            </Section>
          )}
          {hasAlternatives && (
            <Section title="Ingrédients alternatifs IG bas">
              <ul className="space-y-3">
                {cgData.alternative_ingredients.map((alt, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <Leaf className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <AlternativeItem alt={alt} />
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </div>
      )}
    </div>
  );
};

export default RecipeViewPage;
